import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable, Subscription } from 'rxjs';
import { NatService } from 'src/app/services/nat.service';
import { ToastrService } from 'ngx-toastr';
import { ImportWizardService } from '@/app/pages/project/map/repository/import-wizard/import-wizard.service';
import { ImportWizardGeoSpatialService } from '@/app/pages/project/map/repository/import-wizard/components/geospatial/import-wizard-geospatial.service';
import { ImportWizardStorage, TabularColumns, ValidationErrorsInterface } from '@/app/pages/project/map/repository/import-wizard/import-wizard.storage';

export type NatsResponseInterface = {
    status: string;
    rows_validated: number;
    total_rows: number;
    progress_percentage: number;
    errors_found: number;
    valid: boolean;
};

export interface ValidationStartOptions {
    importId: string;
    columns: TabularColumns[];
    page: number;
    pageSize: number;
}

/** 2.5 minutos sem mensagem do NATS → timeout de inatividade */
const INACTIVITY_MS = 2.5 * 60 * 1000;

@Injectable()
export class ImportWizardGeoSpatialValidationHandler implements OnDestroy {

    private readonly _progress$ = new Subject<number>();
    private readonly _label$   = new Subject<string>();
    private readonly _error$   = new Subject<string>();
    private readonly _subs = new Subscription();

    readonly progress$: Observable<number> = this._progress$.asObservable();
    readonly label$:    Observable<string> = this._label$.asObservable();
    readonly error$:    Observable<string> = this._error$.asObservable();

    private _emittedMax = 0;
    private _fakeTimer:       ReturnType<typeof setInterval> | null = null;
    private _inactivityTimer: ReturnType<typeof setTimeout>  | null = null;

    constructor(
        private readonly natsService: NatService,
        private readonly importWizardService: ImportWizardService,
        private readonly importWizardGeoSpatialService: ImportWizardGeoSpatialService,
        private readonly importWizardStorage: ImportWizardStorage,
        private readonly toastrService: ToastrService,
    ) {}

    /**
     * Deve ser chamado no ngOnInit do componente.
     * Subscreve o NATS imediatamente para não perder mensagens.
     */
    init(importId: string, page: number, pageSize: number): void {
        this._subscribeNats(importId, page, pageSize);
    }

    /**
     * Deve ser chamado ao acionar a validação.
     * O NATS já está ouvindo desde o init().
     */
    start({ importId, columns, page, pageSize }: ValidationStartOptions): void {
        this._emittedMax = 0;
        if (this.importWizardStorage.state().hasOccurrences) {
            this._resetInactivityTimer();
        } else {
            this._startFakeProgress();
        }

        const sub = this.importWizardGeoSpatialService
            .postValidateImportWizard(importId)
            .subscribe({
                next: () => {
                    this.importWizardStorage.setDisplayColumns(columns);
                },
                error: (err) => {
                    console.error('[GeoSpatialValidationHandler] Erro ao iniciar validação:', err);
                    this._stopAll();
                    this._error$.next('Erro ao iniciar a validação. Tente novamente.');
                },
            });

        this._subs.add(sub);
    }

    ngOnDestroy(): void {
        this._stopAll();
        this._progress$.complete();
        this._label$.complete();
        this._error$.complete();
        this._subs.unsubscribe();
    }

    // ─── Private ────────────────────────────────────────────────────────────────

    /** Fake progress: 15% do gap restante por tick → assintótico até 90% (só quando hasOccurrences=false) */
    private _startFakeProgress(): void {
        this._stopFakeProgress();
        this._fakeTimer = setInterval(() => {
            if (this._emittedMax < 90) {
                const next = Math.min(this._emittedMax + (90 - this._emittedMax) * 0.15, 89.9);
                this._emitProgress(next);
            }
        }, 300);
    }

    private _stopFakeProgress(): void {
        if (this._fakeTimer !== null) {
            clearInterval(this._fakeTimer);
            this._fakeTimer = null;
        }
    }

    /** Rolling inactivity timer: reset a cada mensagem NATS recebida. */
    private _resetInactivityTimer(): void {
        if (this._inactivityTimer !== null) clearTimeout(this._inactivityTimer);
        this._inactivityTimer = setTimeout(() => {
            this._inactivityTimer = null;
            this._error$.next(
                'Sem resposta do servidor há 2,5 minutos. Verifique a conexão e tente novamente.'
            );
        }, INACTIVITY_MS);
    }

    private _stopAll(): void {
        this._stopFakeProgress();
        if (this._inactivityTimer !== null) {
            clearTimeout(this._inactivityTimer);
            this._inactivityTimer = null;
        }
    }

    /** Emite apenas se o novo valor for maior que o último emitido. */
    private _emitProgress(pct: number): void {
        if (pct > this._emittedMax) {
            this._emittedMax = pct;
            this._progress$.next(pct);
        }
    }

    private _subscribeNats(importId: string, page: number, pageSize: number): void {
        const topic = `import_wizard.${importId}.validation`;
        this.natsService.subscribeToTopic(topic, (msg: string) => {
            this._handleNatsMessage(msg, importId, page, pageSize);
        });
    }

    private _handleNatsMessage(msg: string, importId: string, page: number, pageSize: number): void {
        try {
            const result: NatsResponseInterface = JSON.parse(msg);

            // Cada mensagem reinicia o timer de inatividade (só quando hasOccurrences)
            if (this.importWizardStorage.state().hasOccurrences) {
                this._resetInactivityTimer();

                // Label com contagem de linhas
                if (result.total_rows > 0) {
                    this._label$.next(`Validando linha ${result.rows_validated} de ${result.total_rows}`);
                }
            }

            // Progresso real prevalece; fake não regride
            this._emitProgress(result.progress_percentage);

            if (result.progress_percentage === 100) {
                this._stopAll();
                result.valid
                    ? this._handleSuccess()
                    : this._handleValidationErrors(importId, page, pageSize);
            }
        } catch (error) {
            console.error('[GeoSpatialValidationHandler] Erro ao processar mensagem NATS:', error);
        }
    }

    private _handleSuccess(): void {
        this.toastrService.success('Importação validada com sucesso!');
        
        this.importWizardStorage.setNextStep();
    }

    private _handleValidationErrors(importId: string, page: number, pageSize: number): void {
        const sub = this.importWizardService
            .getImportWizardErrors(importId, page, pageSize)
            .subscribe({
                next: (data) => {
                    this.importWizardStorage.setValidationErrors({
                        items: data.items,
                        total: data.total,
                    } as ValidationErrorsInterface);

                    if (data.total > 0) {
                        this.toastrService.warning('Foram encontrados erros na importação!', '', {
                            closeButton: true,
                        });
                    }

                    this.importWizardStorage.setNextStep();
                },
                error: (error) => {
                    console.error('[GeoSpatialValidationHandler] Erro ao buscar erros de validação:', error);
                },
            });

        this._subs.add(sub);
    }
}